import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import type { FileRecord } from '../services/api';
import './DashboardPage.css';

type SortKey = 'recent' | 'name' | 'size' | 'tables';

export default function DashboardPage() {
  const { user, isAnon } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('recent');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [userOrgs, setUserOrgs] = useState<{ org_id: string; name: string }[]>([]);
  const [shareMenuFileId, setShareMenuFileId] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(() => {
    return localStorage.getItem('nexel_how_it_works') !== 'collapsed';
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayName = user?.displayName || user?.email?.split('@')[0] || (isAnon ? 'Guest' : 'User');

  const formatSize = (bytes: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadFiles = useCallback(async () => {
    try {
      const res = await api.get('/excel/files');
      setFiles(res.data.files || []);
    } catch {
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  useEffect(() => {
    if (user && !isAnon) {
      api.get('/orgs').then(r => setUserOrgs((r.data.orgs || []).map((o: any) => ({ org_id: o.org_id, name: o.name })))).catch(() => {});
    }
  }, [api, user, isAnon]);

  const handleShare = async (fileId: string, orgId: string) => {
    try {
      await api.post(`/excel/files/${fileId}/share`, { org_id: orgId });
      setFiles(prev => prev.map(f => f.file_id === fileId ? { ...f, shared_with_org: orgId } : f));
      setShareMenuFileId(null);
    } catch (err: any) { setError(err.response?.data?.detail || 'Share failed'); }
  };

  const handleUnshare = async (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.post(`/excel/files/${fileId}/unshare`);
      setFiles(prev => prev.map(f => f.file_id === fileId ? { ...f, shared_with_org: undefined } : f));
    } catch (err: any) { setError(err.response?.data?.detail || 'Unshare failed'); }
  };

  const uploadFile = async (file: File) => {
    if (!file.name.match(/\.(xlsx|xlsm|xltx|xltm|xls)$/i)) {
      setError('Only .xlsx, .xls, .xlsm, .xltx, .xltm files are supported');
      return;
    }
    setUploading(true);
    setUploadProgress('Uploading...');
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      setUploadProgress('Processing tables...');
      const res = await api.post('/excel/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadProgress('');
      await loadFiles();
      navigate(`/workspace/${res.data.file_id}`);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Upload failed');
      setUploadProgress('');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleDelete = async (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this file and all its data?')) return;
    try {
      await api.delete(`/excel/files/${fileId}`);
      setFiles(f => f.filter(x => x.file_id !== fileId));
    } catch {
      setError('Delete failed');
    }
  };

  const handleRename = async (fileId: string) => {
    if (!renameValue.trim()) { setRenamingId(null); return; }
    try {
      await api.patch(`/excel/files/${fileId}`, { filename: renameValue.trim() });
      setFiles(prev => prev.map(f => f.file_id === fileId ? { ...f, file_name: renameValue.trim() } : f));
      setRenamingId(null);
    } catch {
      setError('Rename failed');
    }
  };

  const startRename = (f: FileRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(f.file_id);
    setRenameValue(f.file_name);
  };

  const toggleHowItWorks = () => {
    setShowHowItWorks(prev => {
      const next = !prev;
      localStorage.setItem('nexel_how_it_works', next ? 'expanded' : 'collapsed');
      return next;
    });
  };

  // Computed stats
  const totalTables = files.reduce((sum, f) => sum + (f.total_tables || 0), 0);
  const totalSize = files.reduce((sum, f) => sum + ((f as any).file_size_bytes || 0), 0);

  // Filter + sort
  const filtered = files.filter(f => !search || f.file_name.toLowerCase().includes(search.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'name') return a.file_name.localeCompare(b.file_name);
    if (sortBy === 'size') return ((b as any).file_size_bytes || 0) - ((a as any).file_size_bytes || 0);
    if (sortBy === 'tables') return (b.total_tables || 0) - (a.total_tables || 0);
    return (b.created_at || 0) - (a.created_at || 0);
  });

  const hasFiles = files.length > 0;

  return (
    <div className="dashboard">
      {/* Welcome + Stats */}
      <div className="dashboard-welcome">
        <h1 className="dashboard-title">Welcome back, {displayName}</h1>
        <p className="dashboard-subtitle">
          {hasFiles ? `You have ${files.length} file${files.length !== 1 ? 's' : ''} with ${totalTables} table${totalTables !== 1 ? 's' : ''}` : 'Upload Excel files to start querying'}
        </p>
      </div>

      {hasFiles && (
        <div className="dashboard-stats">
          <div className="stat-card">
            <span className="stat-value">{files.length}</span>
            <span className="stat-label">Files</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{totalTables}</span>
            <span className="stat-label">Tables</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{userOrgs.length}</span>
            <span className="stat-label">Orgs</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{totalSize ? formatSize(totalSize) : '—'}</span>
            <span className="stat-label">Used</span>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {hasFiles && (
        <div className="dashboard-quick-actions">
          <button className="quick-action-btn" onClick={() => fileInputRef.current?.click()}>
            <span>📤</span> Upload Excel
          </button>
          <button className="quick-action-btn" onClick={() => {
            const firstFile = sorted[0];
            if (firstFile) navigate(`/workspace/${firstFile.file_id}?tab=chat`);
          }}>
            <span>💬</span> New Chat
          </button>
          {user && !isAnon && (
            <button className="quick-action-btn" onClick={() => navigate('/orgs')}>
              <span>🏢</span> My Orgs
            </button>
          )}
        </div>
      )}

      {/* Upload zone — compact when files exist */}
      {hasFiles ? (
        <div
          className={`upload-compact ${dragOver ? 'drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm,.xltx,.xltm,.xls" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
          {uploading ? (
            <><span className="spinner" style={{ width: 16, height: 16 }} /><span>{uploadProgress}</span></>
          ) : (
            <><span>+ Upload file</span><span className="upload-hint-compact">.xlsx · .xls · up to 100MB</span></>
          )}
        </div>
      ) : (
        <div
          className={`upload-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm,.xltx,.xltm,.xls" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
          {uploading ? (
            <div className="upload-uploading"><span className="spinner" /><span>{uploadProgress}</span></div>
          ) : (
            <>
              <span className="upload-icon">📂</span>
              <p className="upload-text">Drop an Excel file here, or click to browse</p>
              <p className="upload-hint">.xlsx · .xls · .xlsm · .xltx · .xltm · up to 100MB</p>
            </>
          )}
        </div>
      )}

      {error && <div className="dashboard-error">{error} <button onClick={() => setError('')}>✕</button></div>}

      {/* Search + sort bar */}
      {hasFiles && (
        <div className="dashboard-toolbar">
          <input className="dashboard-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search files..." />
          <select className="dashboard-sort" value={sortBy} onChange={e => setSortBy(e.target.value as SortKey)}>
            <option value="recent">Recent</option>
            <option value="name">Name A-Z</option>
            <option value="size">Size</option>
            <option value="tables">Tables</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="dashboard-loading"><span className="spinner" /> Loading files...</div>
      ) : sorted.length === 0 && hasFiles ? (
        <div className="dashboard-empty"><p>No files match "{search}"</p></div>
      ) : sorted.length === 0 ? (
        <div className="dashboard-empty-hero">
          <span className="empty-icon">📊</span>
          <h2 className="empty-title">Your Excel data, finally understood.</h2>
          <p className="empty-desc">
            Upload any Excel file to get started. Nexel will detect every table,
            map formula dependencies, and let you query your data in plain English.
          </p>
          <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()}>
            📤 Upload your first file
          </button>
          <div className="empty-features">
            <div className="empty-feature"><span>🔍</span> Detect tables at any cell position</div>
            <div className="empty-feature"><span>💬</span> Ask questions about your data</div>
            <div className="empty-feature"><span>⚡</span> Filter, sort, aggregate instantly</div>
            <div className="empty-feature"><span>📤</span> Export as Excel, CSV, PDF, Markdown</div>
          </div>
        </div>
      ) : (
        <div className="file-grid">
          {sorted.map(f => (
            <div key={f.file_id} className="file-card card" onClick={() => navigate(`/workspace/${f.file_id}`)}>
              <div className="file-card-header">
                <span className="file-icon">📄</span>
                <div className="file-info">
                  {renamingId === f.file_id ? (
                    <input
                      className="file-rename-input"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(f.file_id); if (e.key === 'Escape') setRenamingId(null); }}
                      onBlur={() => handleRename(f.file_id)}
                      onClick={e => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    <p className="file-name" onDoubleClick={e => startRename(f, e)}>{f.file_name}</p>
                  )}
                  <p className="file-date">
                    {new Date(f.created_at * 1000).toLocaleDateString()}
                    {(f as any).file_size_bytes ? ` · ${formatSize((f as any).file_size_bytes)}` : ''}
                  </p>
                </div>
                <button className="btn btn-ghost file-delete" onClick={e => handleDelete(f.file_id, e)} title="Delete file">🗑</button>
              </div>
              <div className="file-stats">
                <span className="badge badge-accent">{f.total_tables} tables</span>
                <span className="badge badge-accent">{f.sheets?.length || 0} sheets</span>
                {f.total_formulas > 0 && <span className="badge badge-yellow">{f.total_formulas} formulas</span>}
                {f.shared_with_org && <span className="badge badge-green">Shared{userOrgs.find(o => o.org_id === f.shared_with_org) ? ` · ${userOrgs.find(o => o.org_id === f.shared_with_org)!.name}` : ''}</span>}
              </div>
              {userOrgs.length > 0 && (
                <div className="file-share-row">
                  {f.shared_with_org ? (
                    <button className="btn btn-ghost btn-sm file-share-btn" onClick={e => handleUnshare(f.file_id, e)}>Unshare from org</button>
                  ) : userOrgs.length === 1 ? (
                    <button className="btn btn-ghost btn-sm file-share-btn" onClick={e => { e.stopPropagation(); handleShare(f.file_id, userOrgs[0].org_id); }}>
                      Share with {userOrgs[0].name}
                    </button>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <button className="btn btn-ghost btn-sm file-share-btn" onClick={e => { e.stopPropagation(); setShareMenuFileId(shareMenuFileId === f.file_id ? null : f.file_id); }}>
                        Share with org ▾
                      </button>
                      {shareMenuFileId === f.file_id && (
                        <div className="file-share-menu" onClick={e => e.stopPropagation()}>
                          {userOrgs.map(o => (
                            <button key={o.org_id} className="file-share-menu-item" onClick={() => handleShare(f.file_id, o.org_id)}>
                              🏢 {o.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* How Nexel Works — collapsible */}
      <div className="how-it-works">
        <button className="how-it-works-toggle" onClick={toggleHowItWorks}>
          <span>How Nexel Works</span>
          <span className="how-toggle-icon">{showHowItWorks ? '▲' : '▼'}</span>
        </button>
        {showHowItWorks && (
          <div className="how-it-works-content">
            <div className="how-step"><span className="how-step-num">1</span><div><span className="how-step-title">Upload</span><span className="how-step-desc">Drop any Excel file</span></div></div>
            <div className="how-step"><span className="how-step-num">2</span><div><span className="how-step-title">Explore</span><span className="how-step-desc">See every table visualised</span></div></div>
            <div className="how-step"><span className="how-step-num">3</span><div><span className="how-step-title">Ask</span><span className="how-step-desc">Chat with your data in plain English</span></div></div>
            <div className="how-step"><span className="how-step-num">4</span><div><span className="how-step-title">Export</span><span className="how-step-desc">Download results as Excel, CSV, PDF</span></div></div>
          </div>
        )}
      </div>
    </div>
  );
}
